"use client";

import { addAction, addWaypoint, getSelectedPlayer, insertWaypoint } from "@Containers/game/builder/functions/player-functions";
import { getClampedPosition, getNearestPlayer, getPointAtTime, rotatePoint } from "@Containers/game/builder/functions/utilities";
import { TargetMode } from "@Containers/game/builder/models/builder-data-types";
import { useBuilderContext } from "@Context/game/builder/builder-context";
import { ScenarioActionType } from "@Enums/index";
import type { Point } from "@Shared/types/ScenarioTypes";
import { useCallback, useEffect, useRef, useState } from "react";

const spriteScaleBase = 0.66;

export const useViewportLogic = (props: { width: number; height: number }) => {
  const { state, dispatch } = useBuilderContext();
  const viewportRef = useRef<HTMLDivElement>(null);
  const prevDimensions = useRef({ width: 0, height: 0 });

  const [panPoint, setPanPoint] = useState<Point>({ x: 0, y: 0 });
  const [isFieldRecentlyClicked, setIsFieldRecentlyClicked] = useState(false);
  const [isPlayerDeselectClicked, setIsPlayerDeselectClicked] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [isViewportRecentlyPanned, setIsViewportRecentlyPanned] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const calculateConstrainedOffsets = useCallback(
    (offsetX: number, offsetY: number, rect: DOMRect) => {
      const scaledMapWidth = state.field.mapWidth * state.mapScale;
      const scaledMapHeight = state.field.mapHeight * state.mapScale;

      // When the field is exactly the width of the viewport or smaller, center it
      if (scaledMapWidth <= rect.width) {
        offsetX = rect.width / 2;
      } else {
        // Otherwise, constrain panning
        const minOffsetX = rect.width - scaledMapWidth / 2;
        const maxOffsetX = scaledMapWidth / 2;

        offsetX = Math.min(Math.max(offsetX, minOffsetX), maxOffsetX);
      }

      if (scaledMapHeight <= rect.height) {
        offsetY = rect.height / 2;
      } else {
        const minOffsetY = rect.height - scaledMapHeight / 2;
        const maxOffsetY = scaledMapHeight / 2;

        offsetY = Math.min(Math.max(offsetY, minOffsetY), maxOffsetY);
      }

      return { x: offsetX, y: offsetY };
    },
    [state.field.mapWidth, state.field.mapHeight, state.mapScale]
  );

  // Initialize viewport
  useEffect(() => {
    if (!viewportRef.current || isInitialized) return;

    const rect = viewportRef.current.getBoundingClientRect();
    const widthScale = rect.width / state.field.mapWidth;
    const heightScale = rect.height / state.field.mapHeight;
    const minScale = Math.min(widthScale, heightScale);

    const viewOffset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };

    dispatch({
      mapScale: minScale,
      spriteScale: 1 / minScale * spriteScaleBase,
      viewOffset: viewOffset,
    });

    setIsInitialized(true);
  }, [state.field.mapWidth, state.field.mapHeight, isInitialized, dispatch, viewportRef]);

  // Use a timeout to detect double clicks
  useEffect(() => {
    if (isFieldRecentlyClicked) {
      const timeout = setTimeout(() => {
        setIsFieldRecentlyClicked(false);
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [isFieldRecentlyClicked]);

  // Shortly after player deselect, set selectedPlayer to -1
  useEffect(() => {
    if (isPlayerDeselectClicked) {
      const timeout = setTimeout(() => {
        dispatch({ selectedPlayer: -1 });
        setIsPlayerDeselectClicked(false);
      }, 400);

      return () => clearTimeout(timeout);
    }
  }, [isPlayerDeselectClicked, dispatch]);

  // Center view when necessary
  useEffect(() => {
    if (!viewportRef.current) return;

    const rect = viewportRef.current.getBoundingClientRect();
    const viewportWidth = rect.width;
    const viewportHeight = rect.height;
    const mapWidth = state.field.mapWidth * state.mapScale;

    if (mapWidth > viewportWidth) return;

    const viewOffset = {
      x: 0 + viewportWidth / 2,
      y: 0 + viewportHeight / 2,
    };

    dispatch({ viewOffset });
  }, [state.viewRotation, state.mapScale, state.field.mapWidth, dispatch, viewportRef]);

  // Shortly after viewport panning, set isViewportRecentlyPanned to false
  useEffect(() => {
    if (isViewportRecentlyPanned) {
      const timeout = setTimeout(() => {
        setIsViewportRecentlyPanned(false);
      }, 400);

      return () => clearTimeout(timeout);
    }
  }, [isViewportRecentlyPanned]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (!viewportRef.current) return;
      const rect = viewportRef.current.getBoundingClientRect();

      const hasSizeChanged = prevDimensions.current.width !== rect.width || prevDimensions.current.height !== rect.height;

      if (hasSizeChanged) {
        prevDimensions.current = { width: rect.width, height: rect.height };

        const widthScale = rect.width / state.field.mapWidth;
        const heightScale = rect.height / state.field.mapHeight;
        const minScale = Math.min(widthScale, heightScale);

        const worldCenterX = (state.viewOffset.x - rect.width / 2) / state.mapScale + state.field.mapWidth / 2;
        const worldCenterY = (state.viewOffset.y - rect.height / 2) / state.mapScale + state.field.mapHeight / 2;

        const newOffset = {
          x: (worldCenterX - state.field.mapWidth / 2) * minScale + rect.width / 2,
          y: (worldCenterY - state.field.mapHeight / 2) * minScale + rect.height / 2,
        };

        const constrainedOffset = calculateConstrainedOffsets(newOffset.x, newOffset.y, rect);
        dispatch({
          mapScale: minScale,
          spriteScale: 1 / minScale * spriteScaleBase,
          viewOffset: constrainedOffset,
        });
      }
    };

    // Create a ResizeObserver to watch for size changes
    const resizeObserver = new ResizeObserver(handleResize);
    if (viewportRef.current) {
      resizeObserver.observe(viewportRef.current);
    }

    // Initial resize
    handleResize();

    return () => {
      resizeObserver.disconnect();
    };
  }, [state.mapScale, state.viewOffset.x, state.viewOffset.y, state.field.mapWidth, state.field.mapHeight, dispatch, viewportRef]);

  const onWheel = useCallback(
    (e: any) => {
      if (!viewportRef.current) return;

      const rect = viewportRef.current.getBoundingClientRect();
      const widthScale = rect.width / state.field.mapWidth;
      const heightScale = rect.height / state.field.mapHeight;
      const minScale = Math.min(widthScale, heightScale);

      const zoomFactor = e.deltaY < 0 ? 1.05 : 0.95;
      const newScale = Math.max(minScale, Math.min(3, state.mapScale * zoomFactor));

      // Get the mouse position relative to the viewport
      const mousePos = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };

      // Calculate the world position of the mouse
      const worldX = (mousePos.x - state.viewOffset.x) / state.mapScale;
      const worldY = (mousePos.y - state.viewOffset.y) / state.mapScale;

      // Calculate the new offset to keep the mouse position fixed in world space
      const newOffset = {
        x: mousePos.x - worldX * newScale,
        y: mousePos.y - worldY * newScale,
      };

      // If we hit the minimum scale, center the content
      if (newScale === minScale) {
        dispatch({
          mapScale: minScale,
          spriteScale: 1 / minScale * spriteScaleBase,
          viewOffset: {
            x: rect.width / 2,
            y: rect.height / 2,
          },
        });
      } else {
        // Otherwise, apply the new scale and offset, but ensure we stay within bounds
        const constrainedOffset = calculateConstrainedOffsets(newOffset.x, newOffset.y, rect);
        dispatch({
          mapScale: newScale,
          spriteScale: 1 / newScale * spriteScaleBase,
          viewOffset: constrainedOffset,
        });
      }
    },
    [viewportRef, state.field.mapWidth, state.field.mapHeight, state.mapScale, state.viewOffset, calculateConstrainedOffsets, dispatch]
  );

  const handlePanStart = useCallback((e: any) => {
    if (e.buttons !== 4) return;
    const panPoint = { x: e.clientX, y: e.clientY };
    setPanPoint(panPoint);
    setIsPanning(true);
  }, []);

  const handlePanEnd = useCallback(
    (e: any) => {
      if (!isPanning) return;
      setIsPanning(false);
      setIsViewportRecentlyPanned(true);
    },
    [isPanning]
  );

  const handlePan = useCallback(
    (e: any) => {
      if (!isPanning || !viewportRef.current) return;

      const rect = viewportRef.current.getBoundingClientRect();

      // Calculate the new offset without constraints
      const newOffsetX = state.viewOffset.x + (e.clientX - panPoint.x);
      const newOffsetY = state.viewOffset.y + (e.clientY - panPoint.y);

      // Apply constraints
      const constrainedOffset = calculateConstrainedOffsets(newOffsetX, newOffsetY, rect);

      setPanPoint({ x: e.clientX, y: e.clientY });
      dispatch({
        viewOffset: constrainedOffset,
      });
    },
    [isPanning, viewportRef, state.viewOffset, panPoint, calculateConstrainedOffsets, dispatch]
  );

  const handleBallDragged = useCallback(
    (position: Point) => {
      const nearestPlayerIndex = getNearestPlayer(state.clip.scenario.players, position, 0);
      const nearestPlayer = state.clip.scenario.players.get(nearestPlayerIndex);
      let ballPosition = position;
      if (nearestPlayer) {
        const nearestPlayerPosition = getPointAtTime(nearestPlayer?.points, 0, nearestPlayer?.beziers);
        ballPosition = nearestPlayerPosition.position;
      }
      console.log("Nearest player", nearestPlayerIndex);
      dispatch( {
        clip: {
          ...state.clip,
          scenario: {
            ...state.clip.scenario,
            ball: { 
              initialPosition: ballPosition, 
              initialPossessor: nearestPlayerIndex 
            }
          },
        },
      });
    },
    [state.clip.scenario.players, dispatch]
  );

  const handleFieldClick = useCallback(
    (event: any) => {
      // Ignore clicks from panning
      if (isPanning || isViewportRecentlyPanned) return;

      const rect = viewportRef.current?.getBoundingClientRect() || { top: 0, left: 0 };

      const localClickPos = { x: event.clientX - rect.left, y: event.clientY - rect.top };

      // Transform to stage coordinates
      let clickPos = { ...localClickPos };
      clickPos.x -= state.viewOffset.x;
      clickPos.y -= state.viewOffset.y;
      clickPos.x /= state.mapScale;
      clickPos.y /= state.mapScale;
      clickPos.x += state.field.mapWidth / 2;
      clickPos.y += state.field.mapHeight / 2;
      clickPos = rotatePoint(clickPos, state.viewRotation, state.field, true);
      clickPos.x += state.field.mapWidth / 2;
      clickPos.y += state.field.mapHeight / 2;

      // If the field is clicked and a target mode is active, add an action with the click position defining the target
      if (state.targetMode != TargetMode.None) {
        console.log("Handling target mode", state.targetMode);
        let targetPlayer = getNearestPlayer(state.clip.scenario.players, clickPos, state.time);
        const selectedPlayer = getSelectedPlayer(state);
        if (selectedPlayer) {
          if (targetPlayer === state.selectedPlayer) targetPlayer = -1;
          switch (state.targetMode) {
            case TargetMode.Pass:
              addAction(
                state,
                dispatch,
                selectedPlayer,
                targetPlayer != -1 ? ScenarioActionType.PassTarget : ScenarioActionType.ShootDirection,
                targetPlayer,
                clickPos,
                state.time,
                state.time + 1
              );
              break;
            case TargetMode.Shoot:
              addAction(state, dispatch, selectedPlayer, ScenarioActionType.ShootDirection, -1, clickPos, state.time, state.time + 1);
              break;
            case TargetMode.Look:
              addAction(
                state,
                dispatch,
                selectedPlayer,
                targetPlayer != -1 ? ScenarioActionType.LookTarget : ScenarioActionType.Look,
                targetPlayer,
                clickPos,
                state.time,
                state.time + 1
              );
              break;
            case TargetMode.Face:
              console.log("Adding FaceLocation action");
              addAction(state, dispatch, selectedPlayer, ScenarioActionType.FaceLocation, -1, clickPos, state.time, state.time + 1);
              break;
            case TargetMode.InsertWaypointBefore:
              insertWaypoint(state, dispatch, selectedPlayer, state.selectedWaypoint - 1, clickPos, state.time);
              break;
            case TargetMode.InsertWaypointAfter:
              console.log("Inserting waypoint after", state.selectedWaypoint);
              insertWaypoint(state, dispatch, selectedPlayer, state.selectedWaypoint, clickPos, state.time);
              break;
          }
        }

        dispatch({ targetMode: TargetMode.None });
        return;
      }

      // If the field is clicked and a radial is showing, hide the radial
      if (state.showPlayerRadial || state.showFieldRadial || state.showActionRadial || state.showWaypointRadial) {
        dispatch({
          showPlayerRadial: false,
          showFieldRadial: false,
          showActionRadial: false,
          showWaypointRadial: false,
        });
        return;
      }

      // Flag that the field was recently clicked for use in double click detection
      setIsFieldRecentlyClicked(true);

      // If the field is double clicked and no player is selected, show the field radial
      const selectedPlayer = getSelectedPlayer(state);
      if (!selectedPlayer) {
        if (isFieldRecentlyClicked && !state.showFieldRadial) {
          dispatch({
            selectedPosition: clickPos,
            radialAnchor: getClampedPosition(localClickPos, state.radialSize, state.field.mapWidth, state.field.mapHeight),
            showFieldRadial: true,
            showPlayerRadial: false,
            showActionRadial: false,
            showWaypointRadial: false,
          });
        } else if (state.showFieldRadial) {
          dispatch({ showFieldRadial: false });
        }
        return;
      }

      // If the field is double clicked and a player is selected, add a waypoint
      if (isFieldRecentlyClicked && state.selectedPlayer != -1) {
        addWaypoint(state, dispatch, state.selectedPlayer, clickPos);
        setIsPlayerDeselectClicked(false);
        return;
      }

      // If the field is single clicked, deselect any selected player
      if (state.selectedPlayer != -1) {
        setIsPlayerDeselectClicked(true);
      }
    },
    [isPanning, isViewportRecentlyPanned, viewportRef, state, isFieldRecentlyClicked, dispatch]
  );

  return {
    state,
    viewportRef,
    handlePanStart,
    handlePan,
    handlePanEnd,
    onWheel,
    handleBallDragged,
    handleFieldClick,
  };
};
